import { Box, Typography } from "@mui/material";
import ReactiveDialog from "components/common/ReactiveDialog";
import GroupedCheckedDropDown, {
  Option,
} from "components/common/GroupedCheckedDropDown";
import { CalendarIntegration } from "protogen/calendar_service_pb";
import { useEffect, useState } from "react";

interface DefaultCalendarSelectionModalProps {
  open: boolean;
  onClose: () => void;
  onSelect: (integrationRef: string, calendarId: string) => void;
  integrations: CalendarIntegration[];
  currentDefaultIntegrationRef?: string;
  currentDefaultCalendarId?: string;
}

export default function DefaultCalendarSelectionModal({
  open,
  onClose,
  onSelect,
  integrations,
  currentDefaultIntegrationRef = "",
  currentDefaultCalendarId = "",
}: DefaultCalendarSelectionModalProps) {
  const twoWaySyncIntegrations = integrations.filter(
    (integration) =>
      integration.accessScope === "read_write" &&
      integration.status === "active",
  );

  const storedValue =
    currentDefaultIntegrationRef && currentDefaultCalendarId
      ? `${currentDefaultIntegrationRef}:${currentDefaultCalendarId}`
      : "faye";

  const [selectedValue, setSelectedValue] = useState<string>(storedValue);

  useEffect(() => {
    setSelectedValue(storedValue);
  }, [storedValue]);

  const groups: Array<{
    label: string;
    options: Option[];
  }> = [
    {
      label: "Faye",
      options: [
        {
          label: "Faye calendar",
          value: "faye",
          defaultChecked: selectedValue === "faye",
        },
      ],
    },
    ...twoWaySyncIntegrations.map((integration) => ({
      label: integration.name || "Calendar",
      options: (integration.calendars || [])
        .filter((cal) => cal.enabled && cal.role !== "reader")
        .map((cal) => ({
          key: `${integration.ref}:${cal.id}`,
          label: cal.summary,
          value: `${integration.ref}:${cal.id}`,
          defaultChecked: selectedValue === `${integration.ref}:${cal.id}`,
        })),
    })),
  ];

  const handleSave = () => {
    if (selectedValue === "faye") {
      onSelect("", "");
    } else {
      const [integrationRef, calendarId] = selectedValue.split(":");
      onSelect(integrationRef || "", calendarId || "");
    }
    onClose();
  };

  return (
    <ReactiveDialog
      open={open}
      onClose={onClose}
      title="Set default calendar"
      primaryAction={handleSave}
      primaryActionName="Save"
      fullWidthSize="sm"
    >
      <Box display="flex" flexDirection="column" gap="16px">
        <Typography variant="body">
          Choose which calendar should be used by default when creating new
          events.
        </Typography>
        <GroupedCheckedDropDown
          radio
          groups={groups.map((group) => ({
            label: group.label,
            options: group.options,
            onChange: (selected: Option[]) => {
              const validOptions = selected.filter((opt) => opt && opt.value);
              if (validOptions.length > 0) {
                setSelectedValue(validOptions[0].value);
              }
            },
          }))}
          displayValue={
            <Box display="flex" flexDirection="row" gap="4px">
              <Typography variant="body">Default calendar:</Typography>
              <Typography variant="bodyHeavy">
                {groups
                  .flatMap((g) => g.options)
                  .find((opt) => opt.value === selectedValue)?.label ||
                  "Faye calendar"}
              </Typography>
            </Box>
          }
          sx={{ width: "100%" }}
        />
      </Box>
    </ReactiveDialog>
  );
}
